import { createContext, useState, useMemo, useContext, useCallback, useEffect } from 'react';
import { useGeneral } from '../../Context/GeneralContext';

const ParosAutorizadosContext = createContext();

export function ParosAutorizadosProvider(props) {
    const { tokenR } = useGeneral();
    const [items, setItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [nuevo, setNuevo] = useState(false);
    const [editar, setEditar] = useState(false);
    const [snackbar, setSnackbar] = useState(false);
    const [snackbarText, setSnackbarText] = useState('');
    const [errorMsg, setErrorMsg] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const getHeaders = () => ({ authorization: `Bearer ${tokenR.current}`, Accept: 'application/json' });

    const getItems = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/ParosAutorizados/Listado`,
                { headers: getHeaders() }
            );
            if (!res.ok) return;
            const obj = await res.json();
            obj.forEach(item => { item.id = item.codigo; });
            setItems(obj);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR]);

    useEffect(() => { getItems(); }, [getItems]);

    const postItem = useCallback(async (nombre) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/ParosAutorizados/Agregar`,
                {
                    method: 'POST',
                    body: JSON.stringify({ nombre }),
                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) { setErrorMsg('Error al guardar. Intenta de nuevo.'); return false; }
            setSnackbarText('Paro autorizado creado correctamente');
            setSnackbar(true);
            setNuevo(false);
            getItems();
            return true;
        } catch {
            setErrorMsg('Error al guardar. Intenta de nuevo.');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getItems]);

    const putItem = useCallback(async (codigo, nombre) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/ParosAutorizados/Editar`,
                {
                    method: 'PUT',
                    body: JSON.stringify({ codigo, nombre }),
                    headers: { ...getHeaders(), 'Content-Type': 'application/json' },
                }
            );
            if (!res.ok) { setErrorMsg('Error al guardar. Intenta de nuevo.'); return false; }
            setSnackbarText('Paro autorizado actualizado correctamente');
            setSnackbar(true);
            setEditar(false);
            setSelectedItem(null);
            getItems();
            return true;
        } catch {
            setErrorMsg('Error al guardar. Intenta de nuevo.');
            return false;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getItems]);

    const deleteItem = useCallback(async (codigo) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SERVICE_ENDPOINT}/ParosAutorizados/Eliminar?codigo=${codigo}`,
                { method: 'PUT', headers: getHeaders() }
            );
            if (!res.ok) { setSnackbarText('Error al eliminar'); setSnackbar(true); return; }
            setSnackbarText('Paro autorizado eliminado correctamente');
            setSnackbar(true);
            setDeleteConfirm(null);
            getItems();
        } catch {
            setSnackbarText('Error al eliminar');
            setSnackbar(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tokenR, getItems]);

    const value = useMemo(() => ({
        items, getItems, loading,
        selectedItem, setSelectedItem,
        nuevo, setNuevo,
        editar, setEditar,
        snackbar, setSnackbar, snackbarText,
        errorMsg, setErrorMsg,
        deleteConfirm, setDeleteConfirm,
        postItem, putItem, deleteItem,
    }), [
        items, getItems, loading,
        selectedItem,
        nuevo, editar,
        snackbar, snackbarText,
        errorMsg,
        deleteConfirm,
        postItem, putItem, deleteItem,
    ]);

    return <ParosAutorizadosContext.Provider value={value} {...props} />;
}

export function useParosAutorizados() {
    const context = useContext(ParosAutorizadosContext);
    if (!context) throw new Error('useParosAutorizados debe estar dentro del proveedor ParosAutorizadosContext');
    return context;
}
